import React, { useCallback, useMemo, useState } from "react";
import { useForm, useFormState } from "react-final-form";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { FieldArray } from "react-final-form-arrays";
import { get, isEqual } from "lodash";
import classNames from "classnames";
import { Tooltip } from "@blueprintjs/core";
import { PopoverPosition } from "@blueprintjs/core";
import RadioInputGroup from "components/Controls/RadioInputGroup";
import HtmlText from "components/HtmlText";
import ToggleButtonInput from "rs-components/Input/components/ToggleButtonInput";
import Slider from "components/Controls/Slider";
import Input, { FieldStatus } from "rs-components/Input";
import NumberInput from "rs-components/Input/components/NumberInput";
import SelectInput from "rs-components/Input/components/SelectInput";
import SelectItemRenderer from "rs-components/Input/components/SelectInput/components/SelectItemRenderer";
import InfoIconWithTooltip from "components/Tooltips/InfoIconWithTooltip";
import ScoreIcon from "rs-components/ScoreIcon";
import { InsightFormFields } from "../../../..";
import TimeframeDialog from "./components/TimeframeDialog";
import OutputGraph from "./components/OutputGraph";
import IconButton from "components/IconButton";
import { processVariablesSelector } from "store/global/global.selectors";
import { getDefaultTimeFrame, getRecipesCustomVariables, INSIGHT_INPUT_COLORS, } from "store/rs-workspace/utils/scenario.utils";
import { checkCanDisplayScore, checkOutputCombinationIsEssential, getFormattedOutputFormData, getInsightDialogSubmitPayload, getInsightLatestScoreData, getInsightPreviewOperationPending, getOperationSummaryText, getTimeframeValueWithLabel, InsightDialogTestIds, checkBasicScoreCalculationConditions, isTimeframeTimestampFilled, getAssessmentUsedRecipeIds, getActualSourceName, prepareBaseScoreItem, checkScoreErrored, checkScoreHasBiasedValues, } from "store/rs-workspace/utils/insights.utils";
import { insightComparisonOperationsSelector, insightDefaultNameSelector, insightEssentialCombinationsSelector, insightEvaluationOperationsSelector, insightExpandedInputSetIndexSelector, insightOperationDescriptionsSelector, insightOutputOperationOptionsSelector, insightOutputTransformationsOptionsSelector, insightOutputVariableOptionsSelector, persistedInsightsDataSelector, previewRequestsSelector, workspaceCurrentInsightIdSelector, workspaceRecipesSelector, workspaceSimulationsSelector, insightErrorsSelector, recipeCustomVariablesSelector, incompleteSimulationsIdsSelector, workspaceCellModelsSelector, } from "store/rs-workspace/selectors/rs-workspace.selectors";
import { isInsightInputSetCompleted } from "store/rs-workspace/utils/completeness.utils";
import { capitalizeFirstLetterAfterDot, getNumberFromNumberString } from "utils/string";
import { getMinMaxRestrictionsValidator, isNotEmpty } from "utils/validate";
import { CutType } from "shared/types";
import { InsightLabels } from "shared/labels";
import { findDictionaryItemByValue, recipeParameterUnitsType, timeUnits } from "shared/rsDictionary";
import { InsightCutOperations, InsightOutputTypes, InsightPreProcessingTypes, OutputWeight, ProcessVariableType, } from "store/rs-workspace/rs-workspace.types";
import { getInsightScore, setInsightValidationErrors } from "store/rs-workspace/actions/rs-workspace.actions";
import { getFieldRestrictions, getRestrictionDefaultValue, } from "store/knowledge-base-item/knowledge-base-item.utils";
import { getCustomVariableOptions, parseComplexUnitToHtml } from "store/recipe/custom-variables.utils";
import { isEditableSelector } from "store/knowledge-base-item/knowledge-base-item.selectors";
import { NoUnitsType, ParameterTypesCategoryType } from "store/recipe/recipe.types";
import { HelpId, HELP_SYSTEM_URL } from "shared/helpSystemBindings";
import ArrowDownIcon from "icons/long-arrow-down.svg";
import GraphIcon from "icons/insight-graph.svg";
import ErrorIcon from "icons/info_triangle.svg";
import * as styles from "./styles.module.scss";
import * as colors from "styles/colorValues.module.scss";
import * as inputStyles from "rs-components/Input/styles.module.scss";
const weightBasicOptions = [
    { value: OutputWeight.Low, label: 0.5, title: "Low" },
    { value: OutputWeight.Normal, label: 1, title: "Normal" },
    { value: OutputWeight.High, label: 2, title: "High" },
    { value: OutputWeight.VeryHigh, label: 4, title: "Very High" },
];
const cutOperationOptions = [
    { value: InsightCutOperations.Trim, label: InsightCutOperations.Trim },
    { value: InsightCutOperations.Stretch, label: InsightCutOperations.Stretch },
];
const preProcessingTypeOptions = [
    { value: InsightPreProcessingTypes.Absolute, label: InsightPreProcessingTypes.Absolute },
    { value: InsightPreProcessingTypes.Ratio, label: InsightPreProcessingTypes.Ratio },
];
const LowRateOfChangeOperationType = "LowRateOfChange";
const OutputContent = ({ name, index, restrictions }) => {
    const dispatch = useDispatch();
    const { id: workspaceId } = useParams();
    const form = useForm();
    const formState = useFormState();
    const [timeframeDialogOpen, setTimeFrameDialogOpen] = useState(false);
    const [graphDialogIsOpen, setGraphDialogIsOpen] = useState(false);
    const expandedInputSetIndex = useSelector(insightExpandedInputSetIndexSelector);
    const recipes = useSelector(workspaceRecipesSelector);
    const cellModels = useSelector(workspaceCellModelsSelector);
    const workspaceSimulations = useSelector(workspaceSimulationsSelector);
    const editable = useSelector(isEditableSelector);
    const incompleteSimulationIds = useSelector(incompleteSimulationsIdsSelector);
    const expandedInputSetInputs = useMemo(() => {
        const inputSets = get(formState.values, InsightFormFields.inputSets);
        const expandedInputSet = inputSets[expandedInputSetIndex];
        return expandedInputSet?.[InsightFormFields.inputs] ?? [];
    }, [formState.values, expandedInputSetIndex]);
    const usedRecipeIds = useMemo(() => {
        const outputFormValue = get(formState.values, name);
        return getAssessmentUsedRecipeIds(workspaceSimulations, recipes, expandedInputSetInputs, outputFormValue?.[InsightFormFields.type], outputFormValue?.[InsightFormFields.sourceA]?.value, outputFormValue?.[InsightFormFields.sourceB]?.value);
    }, [formState.values, name, recipes, workspaceSimulations, expandedInputSetInputs]);
    const insightDefaultName = useSelector(insightDefaultNameSelector);
    const currentInsightId = useSelector(workspaceCurrentInsightIdSelector);
    const variablesList = useSelector(insightOutputVariableOptionsSelector);
    const customVariablesOptions = useMemo(() => {
        const selectedVariable = get(formState.values, `${name}.${InsightFormFields.processVariable}`);
        return getCustomVariableOptions(usedRecipeIds, recipes, ({ formula, id }) => selectedVariable?.type === ProcessVariableType.Custom && isNotEmpty(selectedVariable?.value)
            ? !!formula?.complete || id === selectedVariable?.value
            : !!formula?.complete);
    }, [recipes, usedRecipeIds, formState.values, name]);
    const variableOptions = useMemo(() => {
        const selectedVariable = get(formState.values, `${name}.${InsightFormFields.processVariable}`);
        return [...variablesList, ...customVariablesOptions]
            ?.filter((item) => {
            if (!item?.recipeId) {
                return true;
            }
            return usedRecipeIds.includes(item?.recipeId);
        })
            .map((item) => {
            const customVariable = getRecipesCustomVariables(recipes, usedRecipeIds)?.find(({ id, recipeId }) => id === item.value && recipeId === item.recipeId);
            return {
                ...item,
                className: classNames({ [styles.title]: item.isGroupName }),
                disabled: selectedVariable?.type === ProcessVariableType.Custom &&
                    selectedVariable?.value === item.value &&
                    selectedVariable?.recipeId === item.recipeId &&
                    !customVariable?.formula?.complete,
            };
        });
    }, [variablesList, customVariablesOptions, usedRecipeIds, formState?.values, name, recipes]);
    const operationOptions = useSelector(insightOutputOperationOptionsSelector(get(formState.values, `${name}.${InsightFormFields.transformationOperation}`) || null, get(formState.values, `${name}.${InsightFormFields.type}`)));
    const evaluationOperationsList = useSelector(insightEvaluationOperationsSelector);
    const comparisonOperationsList = useSelector(insightComparisonOperationsSelector);
    const transformationOptions = useSelector(insightOutputTransformationsOptionsSelector(get(formState.values, `${name}.${InsightFormFields.operation}`) || null, get(formState.values, `${name}.${InsightFormFields.type}`)));
    const essentialCombinations = useSelector(insightEssentialCombinationsSelector);
    const operationDescriptions = useSelector(insightOperationDescriptionsSelector);
    const persistedInsightsData = useSelector(persistedInsightsDataSelector);
    const previewRequests = useSelector(previewRequestsSelector);
    const insightErrors = useSelector(insightErrorsSelector);
    const workspaceRecipes = useSelector(workspaceRecipesSelector);
    const processVariables = useSelector(processVariablesSelector);
    const customVariablesList = useSelector(recipeCustomVariablesSelector(usedRecipeIds));
    const nodeType = useMemo(() => get(formState.values, `${name}.${InsightFormFields.type}`), [formState.values, name]);
    const isComparisonNode = useMemo(() => nodeType === InsightOutputTypes.Comparison, [nodeType]);
    const outputRestrictionsByType = useMemo(() => getFieldRestrictions(restrictions, [nodeType]), [nodeType, restrictions]);
    const preProcessingEnabled = useMemo(() => get(formState.values, `${name}.${InsightFormFields.operation}.${InsightFormFields.preProcessingEnabled}`), [formState.values, name]);
    const isLowRateOfChange = useMemo(() => {
        const selectedOperation = get(formState.values, `${name}.${InsightFormFields.operation}`);
        return (!!selectedOperation && selectedOperation[InsightFormFields.type].value === LowRateOfChangeOperationType);
    }, [formState.values, name]);
    const timeFrameTimestampsIsFilled = useMemo(() => {
        const timeFrame = get(formState.values, `${name}.${InsightFormFields.timeFrame}`);
        return isTimeframeTimestampFilled(timeFrame);
    }, [formState.values, name]);
    const weightOptions = useMemo(() => {
        const selectedOperation = get(formState.values, `${name}.${InsightFormFields.operation}`);
        const timeType = get(formState.values, `${name}.${InsightFormFields.timeFrame}.type`);
        const selectedTransformation = get(formState.values, `${name}.${InsightFormFields.transformationOperation}`);
        return checkOutputCombinationIsEssential(essentialCombinations, selectedOperation?.[InsightFormFields.type]?.value, timeType === CutType.PointInTime
            ? CutType.PointInTime
            : selectedTransformation?.[InsightFormFields.operationType]?.value)
            ? [...weightBasicOptions, { value: OutputWeight.Essential, label: "*", title: "Essential" }]
            : weightBasicOptions;
    }, [essentialCombinations, formState.values, name]);
    const sourceOptions = useMemo(() => expandedInputSetInputs.map((input, index) => ({
        value: input?.[InsightFormFields.id],
        label: `Input ${index + 1}`,
    })), [expandedInputSetInputs]);
    const hasNoSourceOptions = useMemo(() => sourceOptions.length === 0, [sourceOptions]);
    const hasMoreThanOneFilledOutput = useMemo(() => {
        const filledOutputs = get(formState.values, InsightFormFields.outputs).filter((item) => item.type !== null);
        return filledOutputs.length > 1;
    }, [formState.values]);
    const timeframeValueWithLabel = useMemo(() => getTimeframeValueWithLabel(workspaceRecipes, workspaceSimulations, processVariables, expandedInputSetInputs.map((input) => ({
        ...input,
        inputId: input.inputId?.value,
    })), customVariablesList, {
        ...get(formState.values, name),
        sourceA: get(formState.values, `${name}.${InsightFormFields.sourceA}`)?.value,
        sourceB: get(formState.values, `${name}.${InsightFormFields.sourceB}`)?.value,
    }), [
        formState.values,
        name,
        workspaceRecipes,
        workspaceSimulations,
        processVariables,
        expandedInputSetInputs,
        customVariablesList,
    ]);
    const getParameterError = useCallback(() => {
        const fieldName = `${name}.${InsightFormFields.operation}.${InsightFormFields.parameters}`;
        if (insightErrors === null) {
            return "";
        }
        const error = insightErrors.find(({ propertyName }) => propertyName === fieldName);
        if (error) {
            return error.text;
        }
    }, [insightErrors, name]);
    const sourceActualName = useCallback((fieldName) => {
        const source = get(formState.values, `${name}.${fieldName}`);
        if (!isNotEmpty(source)) {
            return null;
        }
        const inputEntity = expandedInputSetInputs.find((item) => item.id === source.value);
        return getActualSourceName(inputEntity, workspaceSimulations);
    }, [formState.values, name, expandedInputSetInputs, workspaceSimulations]);
    const sourceInputBackgroundColor = useCallback((fieldName) => {
        const source = get(formState.values, `${name}.${fieldName}`);
        if (!isNotEmpty(source)) {
            return colors.s200;
        }
        const inputEntity = expandedInputSetInputs.find((item) => item[InsightFormFields.id] === source.value);
        const index = expandedInputSetInputs.indexOf(inputEntity);
        return inputEntity?.inputId !== null ? INSIGHT_INPUT_COLORS[index] : colors.s200;
    }, [formState.values, name, expandedInputSetInputs]);
    const selectedVariableUnits = useMemo(() => {
        const selectedVariable = get(formState.values, `${name}.${InsightFormFields.processVariable}`);
        const isComplexUnit = selectedVariable?.type === ParameterTypesCategoryType.Custom;
        const unit = findDictionaryItemByValue(recipeParameterUnitsType, selectedVariable?.[InsightFormFields.units]);
        if (!isNotEmpty(selectedVariable) ||
            !unit ||
            selectedVariable?.[InsightFormFields.units] === NoUnitsType.NoUnits) {
            return null;
        }
        return isComplexUnit
            ? parseComplexUnitToHtml(selectedVariable?.[InsightFormFields.units])?.html
            : unit.label;
    }, [formState.values, name]);
    const outputScore = useMemo(() => {
        const insightScoreData = getInsightLatestScoreData(persistedInsightsData, currentInsightId);
        const output = get(formState.values, name);
        const expandedInputSet = get(formState.values, `${InsightFormFields.inputSets}[${expandedInputSetIndex}]`);
        return insightScoreData?.scores.find((score) => score.inputSetId === expandedInputSet?.id && score.outputId === output?.id);
    }, [currentInsightId, expandedInputSetIndex, formState.values, name, persistedInsightsData]);
    const outputScoreErrored = useMemo(() => checkScoreErrored(outputScore?.score), [outputScore]);
    const outputScoreHasBiasedValues = useMemo(() => checkScoreHasBiasedValues(outputScore?.score), [outputScore]);
    const advancedSectionScores = useMemo(() => {
        const insightScoreData = getInsightLatestScoreData(persistedInsightsData, currentInsightId);
        const output = get(formState.values, name);
        const outputScores = insightScoreData?.scores.filter((score) => score.outputId === output.id);
        const inputSets = get(formState.values, InsightFormFields.inputSets);
        return inputSets.map((inputSet, inputSetIndex) => {
            const scoreEntity = outputScores?.find((score) => score.inputSetId === inputSet.id);
            const pending = getInsightPreviewOperationPending(previewRequests, currentInsightId, inputSet.id, output.id);
            const canDisplayScore = checkCanDisplayScore(output, inputSet.inputs, processVariables, recipes);
            const score = prepareBaseScoreItem(scoreEntity?.score, canDisplayScore, true);
            const advancedScore = prepareBaseScoreItem(scoreEntity?.score, canDisplayScore);
            return {
                ...score,
                advancedScore: advancedScore?.score,
                pending,
                highlighted: inputSetIndex === expandedInputSetIndex,
            };
        });
    }, [
        persistedInsightsData,
        currentInsightId,
        formState.values,
        name,
        previewRequests,
        processVariables,
        recipes,
        expandedInputSetIndex,
    ]);
    const triggerInsightScoreCalculation = useCallback((outputFormData) => {
        const outputData = getFormattedOutputFormData(outputFormData);
        const insightData = getInsightDialogSubmitPayload(Object(formState.values), parseInt(workspaceId), insightDefaultName);
        const unchangedOutputs = insightData.state.outputs.filter((item) => item.id !== get(formState.values, name).id);
        if (checkBasicScoreCalculationConditions(outputData, processVariables, recipes)) {
            const inputSetsThatUsedInOutput = insightData.state.input.inputSets.filter((inputSet) => isInsightInputSetCompleted(inputSet, [outputData], processVariables, incompleteSimulationIds, workspaceSimulations, cellModels, recipes));
            inputSetsThatUsedInOutput.forEach((inputSet) => {
                dispatch(getInsightScore({
                    insightId: currentInsightId,
                    insightData: {
                        outputId: outputData.id,
                        inputSetId: inputSet.id,
                        combined: false,
                        request: {
                            ...insightData.state,
                            outputs: [...unchangedOutputs, outputData],
                        },
                    },
                }));
            });
        }
    }, [
        formState.values,
        workspaceId,
        insightDefaultName,
        processVariables,
        name,
        incompleteSimulationIds,
        dispatch,
        currentInsightId,
        workspaceSimulations,
        cellModels,
        recipes,
    ]);
    const getOperationData = useCallback((option) => {
        const operationsList = isComparisonNode ? comparisonOperationsList : evaluationOperationsList;
        return operationsList.find((item) => item.type === option?.value);
    }, [isComparisonNode, comparisonOperationsList, evaluationOperationsList]);
    const getOperationParameters = useCallback((option) => {
        const operation = getOperationData(option);
        return operation ? operation.parameters : [];
    }, [getOperationData]);
    const getOperationPreProcessingEnabled = useCallback((option) => {
        const operation = getOperationData(option);
        return !!operation && operation.preProcessingEnabled;
    }, [getOperationData]);
    const triggerOperationChangeCalculation = useCallback((option, updatedWeight) => {
        const parameters = getOperationParameters(option);
        triggerInsightScoreCalculation({
            ...get(formState.values, name),
            ...(updatedWeight ? { [InsightFormFields.weight]: updatedWeight } : {}),
            [InsightFormFields.operation]: {
                [InsightFormFields.type]: option,
                parameters,
            },
        });
    }, [name, getOperationParameters, triggerInsightScoreCalculation, formState.values]);
    const resetOutputValidationError = useCallback(() => {
        const fieldName = `${name}.${InsightFormFields.operation}.${InsightFormFields.parameters}`;
        const updatedInsightErrors = insightErrors?.filter((item) => item.propertyName !== fieldName);
        if (updatedInsightErrors) {
            dispatch(setInsightValidationErrors(updatedInsightErrors));
        }
    }, [insightErrors, dispatch, name]);
    const onOperationChange = useCallback((option) => {
        let updatedWeight = null;
        const selectedTransformation = get(formState.values, `${name}.${InsightFormFields.transformationOperation}`);
        const selectedWeight = get(formState.values, `${name}.${InsightFormFields.weight}`);
        const essentialCombinationSelected = checkOutputCombinationIsEssential(essentialCombinations, option.value, selectedTransformation?.[InsightFormFields.operationType]?.value);
        if (essentialCombinationSelected) {
            form.change(`${name}.${InsightFormFields.weight}`, OutputWeight.Essential);
            updatedWeight = OutputWeight.Essential;
        }
        if (selectedWeight === OutputWeight.Essential && !essentialCombinationSelected) {
            form.change(`${name}.${InsightFormFields.weight}`, OutputWeight.Normal);
            updatedWeight = OutputWeight.Normal;
        }
        form.batch(() => {
            form.change(`${name}.${InsightFormFields.operation}.${InsightFormFields.parameters}`, getOperationParameters(option));
            if (isComparisonNode) {
                form.change(`${name}.${InsightFormFields.operation}.${InsightFormFields.preProcessingEnabled}`, getOperationPreProcessingEnabled(option));
                form.change(`${name}.${InsightFormFields.operation}.${InsightFormFields.preProcessingType}`, InsightPreProcessingTypes.Absolute);
            }
        });
        triggerOperationChangeCalculation(option, updatedWeight);
        resetOutputValidationError();
    }, [
        form,
        formState.values,
        name,
        getOperationParameters,
        triggerOperationChangeCalculation,
        essentialCombinations,
        isComparisonNode,
        getOperationPreProcessingEnabled,
        resetOutputValidationError,
    ]);
    const onTimeFrameChange = useCallback((newValues) => {
        setTimeFrameDialogOpen(false);
        if (!newValues)
            return;
        form.change(`${name}.${InsightFormFields.timeFrame}`, newValues);
        const calculationPayload = get(formState.values, name);
        if (newValues?.type === CutType.PointInTime) {
            form.change(`${name}.${InsightFormFields.transformationOperation}`, null);
            calculationPayload[InsightFormFields.transformationOperation] = null;
        }
        triggerInsightScoreCalculation({
            ...calculationPayload,
            [InsightFormFields.timeFrame]: newValues,
        });
    }, [form, formState.values, name, triggerInsightScoreCalculation]);
    const onTransformationChange = useCallback((option) => {
        const selectedOperation = get(formState.values, `${name}.${InsightFormFields.operation}`);
        const selectedWeight = get(formState.values, `${name}.${InsightFormFields.weight}`);
        let updatedWeight = selectedWeight;
        const essentialCombinationSelected = checkOutputCombinationIsEssential(essentialCombinations, selectedOperation?.[InsightFormFields.type]?.value, option.value);
        if (essentialCombinationSelected) {
            form.change(`${name}.${InsightFormFields.weight}`, OutputWeight.Essential);
            updatedWeight = OutputWeight.Essential;
        }
        if (selectedWeight === OutputWeight.Essential && !essentialCombinationSelected) {
            form.change(`${name}.${InsightFormFields.weight}`, OutputWeight.Normal);
            updatedWeight = OutputWeight.Normal;
        }
        triggerInsightScoreCalculation({
            ...get(formState.values, name),
            [InsightFormFields.weight]: updatedWeight,
            [InsightFormFields.transformationOperation]: {
                [InsightFormFields.operationType]: option,
                parameter: null,
            },
        });
    }, [essentialCombinations, form, triggerInsightScoreCalculation, formState.values, name]);
    const onVariableChange = useCallback((option) => {
        const selectedOperation = get(formState.values, `${name}.${InsightFormFields.operation}`);
        const selectedTransformation = get(formState.values, `${name}.${InsightFormFields.transformationOperation}`);
        const selectedTimeFrame = get(formState.values, `${name}.${InsightFormFields.timeFrame}`);
        const calculationPayload = {
            ...get(formState.values, name),
            [InsightFormFields.processVariable]: option,
        };
        if (!selectedOperation) {
            const defaultOperation = getRestrictionDefaultValue(outputRestrictionsByType, [capitalizeFirstLetterAfterDot(`${InsightFormFields.operation}.${InsightFormFields.type}`)], option.value);
            const defaultOption = operationOptions.find((item) => item.value === defaultOperation);
            const operationParameters = getOperationParameters(defaultOption);
            const operationPayload = defaultOption
                ? {
                    [InsightFormFields.type]: defaultOption,
                    [InsightFormFields.parameters]: operationParameters,
                    ...(isComparisonNode
                        ? {
                            [InsightFormFields.preProcessingEnabled]: getOperationPreProcessingEnabled(defaultOption),
                            [InsightFormFields.preProcessingType]: InsightPreProcessingTypes.Absolute,
                        }
                        : {}),
                }
                : null;
            form.change(`${name}.${InsightFormFields.operation}`, operationPayload);
            calculationPayload[InsightFormFields.operation] = operationPayload;
        }
        if (!selectedTransformation &&
            isEqual(getDefaultTimeFrame(outputRestrictionsByType), selectedTimeFrame)) {
            const defaultTransformation = getRestrictionDefaultValue(outputRestrictionsByType, [InsightFormFields.transformationOperation, InsightFormFields.operationType], option.value);
            const defaultOption = transformationOptions.find((item) => item.value === defaultTransformation);
            if (defaultTransformation === CutType.PointInTime) {
                form.change(`${name}.${InsightFormFields.timeFrame}`, { type: CutType.PointInTime });
                calculationPayload[InsightFormFields.timeFrame] = { type: CutType.PointInTime };
            }
            else {
                form.change(`${name}.${InsightFormFields.transformationOperation}.${InsightFormFields.operationType}`, defaultOption);
                calculationPayload[InsightFormFields.transformationOperation] = defaultOption
                    ? {
                        [InsightFormFields.operationType]: defaultOption,
                        parameter: null,
                    }
                    : null;
            }
        }
        const essentialCombinationSelected = checkOutputCombinationIsEssential(essentialCombinations, calculationPayload?.[InsightFormFields.operation]?.[InsightFormFields.type]?.value, calculationPayload?.[InsightFormFields.transformationOperation]?.[InsightFormFields.operationType]);
        if (essentialCombinationSelected) {
            form.change(`${name}.${InsightFormFields.weight}`, OutputWeight.Essential);
            calculationPayload[InsightFormFields.weight] = OutputWeight.Essential;
        }
        triggerInsightScoreCalculation(calculationPayload);
    }, [
        formState.values,
        name,
        outputRestrictionsByType,
        essentialCombinations,
        triggerInsightScoreCalculation,
        operationOptions,
        getOperationParameters,
        isComparisonNode,
        getOperationPreProcessingEnabled,
        form,
        transformationOptions,
    ]);
    const onOperationDetailBlur = useCallback(() => {
        const operation = get(formState.values, `${name}.${InsightFormFields.operation}`);
        triggerInsightScoreCalculation({
            ...get(formState.values, name),
            [InsightFormFields.operation]: {
                ...operation,
                parameters: operation[InsightFormFields.parameters].map((item) => ({
                    ...item,
                    value: getNumberFromNumberString(item.value, false),
                })),
            },
        });
        resetOutputValidationError();
    }, [formState.values, name, triggerInsightScoreCalculation, resetOutputValidationError]);
    const onTimeWindowUnitsChange = useCallback((option) => {
        const operation = get(formState.values, `${name}.${InsightFormFields.operation}`);
        triggerInsightScoreCalculation({
            ...get(formState.values, name),
            [InsightFormFields.operation]: {
                ...operation,
                [InsightFormFields.timeWindow]: {
                    [InsightFormFields.value]: getNumberFromNumberString(operation?.[InsightFormFields.timeWindow]?.[InsightFormFields.value], false),
                    [InsightFormFields.units]: option,
                },
            },
        });
    }, [formState.values, name, triggerInsightScoreCalculation]);
    const onUpdateWeight = useCallback((value) => {
        triggerInsightScoreCalculation({
            ...get(formState.values, name),
            [InsightFormFields.weight]: value,
        });
    }, [name, formState.values, triggerInsightScoreCalculation]);
    const onUpdateCutOperation = useCallback((value) => {
        triggerInsightScoreCalculation({
            ...get(formState.values, name),
            [InsightFormFields.cutOperation]: value,
        });
    }, [name, formState.values, triggerInsightScoreCalculation]);
    const onChangePreProcessingType = useCallback((value) => {
        const operation = get(formState.values, `${name}.${InsightFormFields.operation}`);
        triggerInsightScoreCalculation({
            ...get(formState.values, name),
            [InsightFormFields.operation]: {
                ...operation,
                [InsightFormFields.preProcessingType]: value,
            },
        });
    }, [name, formState.values, triggerInsightScoreCalculation]);
    const onChangeAdvancedEnabled = useCallback((advancedEnabled) => {
        if (!advancedEnabled) {
            form.change(`${name}.${InsightFormFields.advanced}.${InsightFormFields.robustness}`, 0);
            form.change(`${name}.${InsightFormFields.advanced}.${InsightFormFields.reliability}`, 0);
        }
        triggerInsightScoreCalculation({
            ...get(formState.values, name),
            [InsightFormFields.advancedEnabled]: advancedEnabled,
        });
    }, [form, formState.values, name, triggerInsightScoreCalculation]);
    const onChangeSource = useCallback((fieldName, option) => {
        const calculateScorePayload = { ...get(formState.values, name), [fieldName]: option };
        const currentSourceA = get(formState.values, `${name}.${InsightFormFields.sourceA}`);
        const currentSourceB = get(formState.values, `${name}.${InsightFormFields.sourceB}`);
        if (isComparisonNode) {
            if (fieldName === InsightFormFields.sourceA &&
                isNotEmpty(currentSourceB) &&
                currentSourceB.value === option.value) {
                calculateScorePayload[InsightFormFields.sourceB] = currentSourceA;
                form.change(`${name}.${InsightFormFields.sourceB}`, currentSourceA);
            }
            if (fieldName === InsightFormFields.sourceB &&
                isNotEmpty(currentSourceA) &&
                currentSourceA.value === option.value) {
                calculateScorePayload[InsightFormFields.sourceA] = currentSourceB;
                form.change(`${name}.${InsightFormFields.sourceA}`, currentSourceB);
            }
        }
        triggerInsightScoreCalculation(calculateScorePayload);
    }, [triggerInsightScoreCalculation, formState.values, name, isComparisonNode, form]);
    const operationSummaryText = useMemo(() => {
        const description = isComparisonNode
            ? operationDescriptions.comparisonDescriptions
            : operationDescriptions.evaluationDescriptions;
        return getOperationSummaryText(description, get(formState.values, `${name}.${InsightFormFields.processVariable}`), get(formState.values, `${name}.${InsightFormFields.operation}`), get(formState.values, `${name}.${InsightFormFields.transformationOperation}`), get(formState.values, `${name}.${InsightFormFields.timeFrame}`)?.type === CutType.PointInTime, timeframeValueWithLabel?.label);
    }, [
        formState.values,
        isComparisonNode,
        name,
        operationDescriptions.comparisonDescriptions,
        operationDescriptions.evaluationDescriptions,
        timeframeValueWithLabel?.label,
    ]);
    return (React.createElement("div", { className: styles.root },
        React.createElement("div", { className: styles.source, "data-testid": InsightDialogTestIds.EvaluationSource },
            React.createElement("div", { className: classNames(styles.sourceA, { [styles.fullWidth]: !isComparisonNode }) },
                React.createElement(Input, { name: `${name}.${InsightFormFields.sourceA}`, component: SelectInput, items: sourceOptions, label: isComparisonNode ? "Source A" : "Source", placeholder: "Select", itemRenderer: SelectItemRenderer, onChange: (option) => onChangeSource(InsightFormFields.sourceA, option), restrictions: getFieldRestrictions(outputRestrictionsByType, [InsightFormFields.sourceA]), classes: {
                        container: styles.selectWrapper,
                        component: styles.select,
                    }, componentClasses: {
                        selectButton: classNames({ [styles.withPadding]: !hasNoSourceOptions }),
                    }, selectButtonProps: {
                        prefix: hasNoSourceOptions ? null : (React.createElement("div", { className: styles.palette, style: {
                                backgroundColor: sourceInputBackgroundColor(InsightFormFields.sourceA),
                            } })),
                    }, readonly: !editable }),
                !!sourceActualName(InsightFormFields.sourceA) && (React.createElement("p", { className: styles.name }, sourceActualName(InsightFormFields.sourceA)))),
            isComparisonNode && (React.createElement("div", { className: styles.sourceB },
                React.createElement(Input, { name: `${name}.${InsightFormFields.sourceB}`, component: SelectInput, items: sourceOptions, label: "Source B", placeholder: "Select", itemRenderer: SelectItemRenderer, onChange: (option) => onChangeSource(InsightFormFields.sourceB, option), restrictions: getFieldRestrictions(outputRestrictionsByType, [InsightFormFields.sourceB]), classes: {
                        container: styles.selectWrapper,
                        component: styles.select,
                    }, componentClasses: {
                        selectButton: classNames({ [styles.withPadding]: !hasNoSourceOptions }),
                    }, selectButtonProps: {
                        prefix: hasNoSourceOptions ? null : (React.createElement("div", { className: styles.palette, style: {
                                backgroundColor: sourceInputBackgroundColor(InsightFormFields.sourceB),
                            } })),
                    }, readonly: !editable }),
                !!sourceActualName(InsightFormFields.sourceB) && (React.createElement("p", { className: styles.name }, sourceActualName(InsightFormFields.sourceB))))),
            isComparisonNode && (React.createElement("div", { className: styles.cutOperation },
                React.createElement(Input, { component: RadioInputGroup, name: `${name}.${InsightFormFields.cutOperation}`, onChange: onUpdateCutOperation, items: cutOperationOptions, classes: { container: styles.radioInputContainer }, restrictions: getFieldRestrictions(outputRestrictionsByType, [InsightFormFields.cutOperation]), readonly: !editable }),
                React.createElement("div", { className: styles.infoTooltipBig },
                    React.createElement(InfoIconWithTooltip, { classes: {
                            className: styles["info-icon"],
                        }, tooltipText: InsightLabels.CUT_OPERATION_HINT }))))),
        React.createElement("div", { className: styles.evaluation },
            React.createElement("h5", { className: styles.title }, "Assessment"),
            React.createElement("div", { className: classNames(styles.field, styles.variable) },
                React.createElement(Input, { name: `${name}.${InsightFormFields.processVariable}`, component: SelectInput, items: variableOptions, label: "Variable", placeholder: "Select", itemRenderer: SelectItemRenderer, onChange: onVariableChange, restrictions: getFieldRestrictions(outputRestrictionsByType, [InsightFormFields.processVariable]), componentClasses: {
                        portalClassName: styles.selectPortal,
                    }, classes: {
                        container: styles.selectWrapper,
                        component: styles.select,
                    }, readonly: !editable }),
                selectedVariableUnits && (React.createElement("p", { className: styles.name, dangerouslySetInnerHTML: { __html: selectedVariableUnits } }))),
            React.createElement("div", { className: styles.field },
                React.createElement(Input, { name: `${name}.${InsightFormFields.operation}.${InsightFormFields.type}`, component: SelectInput, items: operationOptions, label: "Operation", infoIconText: isComparisonNode
                        ? InsightLabels.COMPARISON_OPERATIONS_HINT
                        : InsightLabels.EVALUATION_OPERATIONS_HINT, learnMoreUrl: `${HELP_SYSTEM_URL}#cshid=${HelpId.INSIGHTS_OPERATIONS}`, restrictions: getFieldRestrictions(outputRestrictionsByType, [InsightFormFields.operation]), placeholder: "Select", itemRenderer: SelectItemRenderer, onChange: onOperationChange, classes: {
                        container: styles.selectWrapper,
                        component: styles.select,
                        infoTooltip: styles.infoTooltip,
                    }, readonly: !editable }),
                isComparisonNode && preProcessingEnabled && (React.createElement("div", { className: styles.preProcessingType },
                    React.createElement(Input, { component: RadioInputGroup, name: `${name}.${InsightFormFields.operation}.${InsightFormFields.preProcessingType}`, onChange: onChangePreProcessingType, items: preProcessingTypeOptions, classes: { container: styles.radioInputContainer }, restrictions: getFieldRestrictions(outputRestrictionsByType, [
                            InsightFormFields.operation,
                            InsightFormFields.preProcessingType,
                        ]), readonly: !editable }))),
                React.createElement("div", { className: styles.details },
                    isLowRateOfChange && (React.createElement("div", { key: `${name}.${InsightFormFields.operation}.${InsightFormFields.timeWindow}`, className: styles.timeWindow },
                        React.createElement(Input, { name: `${name}.${InsightFormFields.operation}.${InsightFormFields.timeWindow}.${InsightFormFields.value}`, component: NumberInput, label: "Time window", onBlur: () => onOperationDetailBlur(), restrictions: {
                                IsNumber: true,
                                ...getFieldRestrictions(outputRestrictionsByType, [
                                    InsightFormFields.operation,
                                    LowRateOfChangeOperationType,
                                    InsightFormFields.timeWindow,
                                    InsightFormFields.value,
                                ]),
                            }, prefix: "+-", classes: {
                                container: styles.detail,
                                label: styles.label,
                            }, readonly: !editable }),
                        React.createElement(Input, { name: `${name}.${InsightFormFields.operation}.${InsightFormFields.timeWindow}.${InsightFormFields.units}`, component: SelectInput, items: timeUnits, label: "", placeholder: "Select", restrictions: getFieldRestrictions(outputRestrictionsByType, [
                                InsightFormFields.operation,
                                LowRateOfChangeOperationType,
                                InsightFormFields.timeWindow,
                                InsightFormFields.units,
                            ]), onChange: onTimeWindowUnitsChange, itemRenderer: SelectItemRenderer, classes: {
                                container: styles.selectWrapper,
                                component: styles.select,
                            }, readonly: !editable }))),
                    React.createElement(FieldArray, { name: `${name}.${InsightFormFields.operation}.${InsightFormFields.parameters}` }, ({ fields, meta }) => fields.map((fieldName) => {
                        const operationType = get(formState.values, `${name}.${InsightFormFields.operation}.${InsightFormFields.type}`)?.value;
                        const fieldRestrictions = getFieldRestrictions(outputRestrictionsByType, [
                            InsightFormFields.operation,
                            operationType,
                            InsightFormFields.parameters,
                            isComparisonNode
                                ? "ComparisonInsightOperationParameter"
                                : "EvaluationInsightOperationParameter",
                        ]);
                        const valueValueRestrictions = getFieldRestrictions(fieldRestrictions, ["Value.Value"]);
                        const { MinValue = undefined, MinValueEqual = undefined } = { ...valueValueRestrictions };
                        const parameterType = get(formState.values, `${fieldName}.${InsightFormFields.parameterType}`);
                        return (React.createElement(Input, { key: `${fieldName}.${InsightFormFields.value}`, name: `${fieldName}.${InsightFormFields.value}`, component: NumberInput, label: get(formState.values, `${fieldName}.${InsightFormFields.name}`), onBlur: () => meta.dirty && onOperationDetailBlur(), restrictions: {
                                IsNumber: true,
                                ...getFieldRestrictions(fieldRestrictions, ["Value"]),
                            }, customValidator: getMinMaxRestrictionsValidator({
                                ...valueValueRestrictions,
                                MinValue: isNotEmpty(MinValue)
                                    ? Array.isArray(MinValue)
                                        ? MinValue
                                        : [MinValue]
                                    : undefined,
                                MinValueEqual,
                            }, parameterType), classes: {
                                container: styles.detail,
                                label: styles.label,
                            }, readonly: !editable }));
                    })),
                    !!getParameterError() && React.createElement("div", { className: styles.errorMessage }, getParameterError()))),
            React.createElement("div", { className: classNames(styles.field, styles.flex) },
                React.createElement(Input, { name: `${name}.${InsightFormFields.timeFrame}`, component: SelectInput, itemRenderer: SelectItemRenderer, label: "Timeframe", placeholder: "Select", items: [{}], value: timeframeValueWithLabel, disableFormChange: true, popoverProps: { onInteraction: setTimeFrameDialogOpen }, componentClasses: {
                        portalClassName: styles.selectPortal,
                        selectButton: styles["timeFrame-selectButton"],
                    }, classes: {
                        container: classNames(styles.selectWrapper, {
                            [inputStyles[FieldStatus.EMPTY]]: !timeFrameTimestampsIsFilled,
                        }),
                        component: classNames(styles.select, styles["timeFrame-select"], {
                            [styles.hasBiasedValues]: outputScoreHasBiasedValues,
                        }),
                    }, restrictions: getFieldRestrictions(outputRestrictionsByType, [InsightFormFields.timeFrame]), readonly: !editable }),
                outputScoreHasBiasedValues && (React.createElement("div", { className: styles.biasedInfo },
                    React.createElement(Tooltip, { minimal: true, content: React.createElement("p", null, InsightLabels.BIASED_VALUES_SCORE), position: PopoverPosition.BOTTOM, portalClassName: styles.tooltipPortal, popoverClassName: styles.tooltip, boundary: "clippingParents", openOnTargetFocus: false },
                        React.createElement(ErrorIcon, { className: styles["error-icon"] })))),
                timeframeDialogOpen && (React.createElement(TimeframeDialog, { name: name, onClose: onTimeFrameChange, outputIndex: index, inputs: expandedInputSetInputs, recipeIds: usedRecipeIds, restrictions: {
                        ...getFieldRestrictions(outputRestrictionsByType, [InsightFormFields.timeFrame]),
                        Type: getFieldRestrictions(outputRestrictionsByType, [`${InsightFormFields.timeFrame}.Type`]),
                    } }))),
            get(formState.values, `${name}.${InsightFormFields.timeFrame}`)?.type !== CutType.PointInTime && (React.createElement("div", { className: styles.field },
                React.createElement(Input, { name: `${name}.${InsightFormFields.transformationOperation}.${InsightFormFields.operationType}`, component: SelectInput, items: transformationOptions.filter(({ value }) => value !== CutType.PointInTime), label: "Transformation", placeholder: "Select", infoIconText: isComparisonNode
                        ? InsightLabels.TRANSFORMATIONS_COMPARISON_HINT
                        : InsightLabels.TRANSFORMATIONS_EVALUATION_HINT, learnMoreUrl: `${HELP_SYSTEM_URL}#cshid=${HelpId.INSIGHTS_TRANSFORMATION}`, itemRenderer: SelectItemRenderer, onChange: onTransformationChange, classes: {
                        container: styles.selectWrapper,
                        component: styles.select,
                        infoTooltip: styles.infoTooltip,
                    }, restrictions: getFieldRestrictions(outputRestrictionsByType, [
                        InsightFormFields.transformationOperation,
                        InsightFormFields.operationType,
                    ]), readonly: !editable }))),
            React.createElement("div", { className: styles.summary },
                React.createElement(IconButton, { onClick: () => setGraphDialogIsOpen(true), icon: React.createElement(GraphIcon, null), title: "Graph", disabled: get(formState.values, `${name}.${InsightFormFields.advancedEnabled}`), classes: {
                        root: classNames(styles["summary-graphIcon"], {
                            [styles["summary-graphIcon-error"]]: outputScoreErrored || outputScoreHasBiasedValues,
                        }),
                    } }),
                graphDialogIsOpen && (React.createElement(OutputGraph, { index: index, name: name, score: outputScore, operationSummaryText: operationSummaryText, onClose: () => setGraphDialogIsOpen(false) })),
                React.createElement(HtmlText, { targetClassName: styles["summary-text"], text: operationSummaryText })),
            React.createElement("div", { className: styles.advanced },
                React.createElement(Input, { component: ToggleButtonInput, form: form, name: `${name}.${InsightFormFields.advancedEnabled}`, onChange: onChangeAdvancedEnabled, restrictions: getFieldRestrictions(outputRestrictionsByType, [InsightFormFields.advancedEnabled]), disabled: !editable }, "Advanced"),
                get(formState.values, `${name}.${InsightFormFields.advancedEnabled}`) && (React.createElement(React.Fragment, null,
                    React.createElement("div", { className: styles.scores }, advancedSectionScores.map((scoreItem, i) => (React.createElement("div", { key: `advanced-${i}`, className: styles.column },
                        React.createElement(ScoreIcon, { ...scoreItem }),
                        React.createElement(ArrowDownIcon, { className: styles.icon }),
                        React.createElement(ScoreIcon, { ...scoreItem, score: scoreItem.advancedScore }))))),
                    React.createElement("div", { className: styles.sliders },
                        React.createElement("div", { className: styles.robustness },
                            React.createElement("div", { className: styles.name },
                                "Robustness*",
                                React.createElement(InfoIconWithTooltip, { classes: { className: styles.infoTooltip }, tooltipText: isComparisonNode
                                        ? InsightLabels.ROBUSTNESS_COMPARISON_HINT
                                        : InsightLabels.ROBUSTNESS_EVALUATION_HINT })),
                            React.createElement(Slider, { stepSize: 0.1, value: get(formState.values, `${name}.${InsightFormFields.advanced}.${InsightFormFields.robustness}`), onChange: (value) => form.change(`${name}.${InsightFormFields.advanced}.${InsightFormFields.robustness}`, value), onUpdateValue: (value) => triggerInsightScoreCalculation({
                                    ...get(formState.values, name),
                                    [InsightFormFields.advanced]: {
                                        ...get(formState.values, `${name}.${InsightFormFields.advanced}`),
                                        [InsightFormFields.robustness]: value,
                                    },
                                }), min: 0, max: 1, fullSize: true, classes: { slider: styles.slider }, disabled: !editable }),
                            React.createElement("div", { className: styles.sliderInfo },
                                React.createElement("span", null, "not important"),
                                React.createElement("span", null, "very important"))),
                        React.createElement("div", { className: styles.reliability },
                            React.createElement("div", { className: styles.name },
                                "Reliability*",
                                React.createElement(InfoIconWithTooltip, { classes: { className: styles.infoTooltip }, tooltipText: isComparisonNode
                                        ? InsightLabels.RELIABILITY_COMPARISON_HINT
                                        : InsightLabels.RELIABILITY_EVALUATION_HINT })),
                            React.createElement(Slider, { disabled: !editable, stepSize: 0.1, value: get(formState.values, `${name}.${InsightFormFields.advanced}.${InsightFormFields.reliability}`), onChange: (value) => form.change(`${name}.${InsightFormFields.advanced}.${InsightFormFields.reliability}`, value), onUpdateValue: (value) => triggerInsightScoreCalculation({
                                    ...get(formState.values, name),
                                    [InsightFormFields.advanced]: {
                                        ...get(formState.values, `${name}.${InsightFormFields.advanced}`),
                                        [InsightFormFields.reliability]: value,
                                    },
                                }), min: 0, max: 1, fullSize: true, classes: { slider: styles.slider } }),
                            React.createElement("div", { className: styles.sliderInfo },
                                React.createElement("span", null, "not reliable"),
                                React.createElement("span", null, "very reliable"))))))),
            hasMoreThanOneFilledOutput && (React.createElement("div", { className: styles.weight },
                React.createElement("div", { className: styles.name }, "Weight*"),
                React.createElement(Input, { component: RadioInputGroup, name: `${name}.${InsightFormFields.weight}`, onChange: onUpdateWeight, items: weightOptions, classes: { container: styles.radioInputContainer }, restrictions: getFieldRestrictions(outputRestrictionsByType, [InsightFormFields.weight]), readonly: !editable }))))));
};
export default OutputContent;
